/**
 * Which brand a screen / document carries, decided by the domain the app is opened on — the same rule as the browser
 * tab title in public/index.html and the login page (Components/Login/LoginWrapper.js):
 *
 *   host (or the host remembered at login) === REACT_APP_DOMAIN_2  ->  Agaram Brokers
 *   anything else                                                   ->  Rayal Brokers
 *
 * Two logo files per brand in /public:
 *   logoFile      the icon mark (the tab icon);
 *   wordmarkFile  the mark WITH the company name, as one designed lockup (what the login page shows, trimmed of its
 *                 empty margins). A document header uses this: a mark next to separately typed text never looked like
 *                 one logo.
 * `accent` is the brand's colour for the thin rule under a document header.
 *
 * And `navLogoUrl`, the same mark in colours made for a dark background (the navy top bar): the blue parts of both logos
 * would disappear there, so Rayal is a brighter blue and Agaram a white shield with its gold check. These two are imported,
 * not in /public: under 10 KB the build puts a picture inside the app itself, so it is on screen the moment the bar is. A
 * file in /public only starts downloading then, and the owner saw an empty ribbon for seconds after login.
 */

import RayalLogoOnDark from "../../../../Resources/Images/RayalLogoOnDark.png";
import AgaramLogoOnDark from "../../../../Resources/Images/AgaramLogoOnDark.png";

export const RAYAL_BRAND = {
  key: "rayal",
  name: "Rayal Brokers",
  filePrefix: "RayalBrokers",
  logoFile: "rayalLogo.png",
  navLogoUrl: RayalLogoOnDark,
  wordmarkFile: "RayalWordmark.png",
  accent: [0, 112, 255],
};

export const AGARAM_BRAND = {
  key: "agaram",
  name: "Agaram Brokers",
  filePrefix: "AgaramBrokers",
  logoFile: "AgaramLogo.png",
  navLogoUrl: AgaramLogoOnDark,
  wordmarkFile: "AgaramWordmark.png",
  accent: [201, 162, 58],
};

/** Pure: the brand for a host. `agaramDomain` empty / undefined never matches anything. */
export const resolveBrand = ({ hostname = "", sessionDomain = "", agaramDomain = "" } = {}) => {
  const wanted = String(agaramDomain || "").trim();
  if (wanted && (hostname === wanted || sessionDomain === wanted)) return AGARAM_BRAND;
  return RAYAL_BRAND;
};

/** The brand of the page being viewed, with the URL of its logo. Never throws. */
export const currentBrand = () => {
  let hostname = "";
  let sessionDomain = "";
  try {
    hostname = window.location.hostname;
  } catch (e) {
    /* not in a browser */
  }
  try {
    sessionDomain = sessionStorage.getItem("currentDomain") || "";
  } catch (e) {
    /* storage blocked */
  }
  const brand = resolveBrand({ hostname, sessionDomain, agaramDomain: process.env.REACT_APP_DOMAIN_2 });
  const base = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return {
    ...brand,
    logoUrl: `${base}/${brand.logoFile}`,
    wordmarkUrl: `${base}/${brand.wordmarkFile}`,
  };
};
